import React from "react";
import PropTypes from "prop-types";
import { Button, Type } from "@ebscois/react-component-library";
import { logTopicPrintConfirmClicked } from "../../util/analytics";
import {
  DialogWrapper,
  DialogHeadingWrapper,
  DialogContentWrapper,
  DialogSubHeadingWrapper,
  DialogContentHeader,
  SelectButton,
  DialogContentList,
  DialogParentToc,
  DialogChildToc,
  DialogContentListItemParent,
  DialogContentFooter,
} from "./_.styled";

const ContentPrintDialog = ({
  isMobile,
  isOpen,
  onCancel,
  onPrint,
  selectedReferences,
  onParentChange,
  onChildChange,
  handlePrintDialogSelectAll,
  handlePrintDialogDeselectAll,
  data,
  tocReferences,
  canSelectSubsectionsForPrint,
  disablePrint,
  telemetryData,
}) => {
  const handleOnPrint = () => {
    logTopicPrintConfirmClicked({
      title: telemetryData.title,
      recordAn: telemetryData.recordAn,
    });
    onPrint();
  };

  const maxHeight = isMobile ? "68vh" : "350px";

  return data?.length > 0 ? (
    <DialogWrapper
      onRequestClose={onCancel}
      isOpen={isOpen}
      data-testid="dialog-print-wrapper"
      maxWidth="md"
      hasMaxHeight={true}
    >
      <DialogHeadingWrapper>
        <Type
          element="h2"
          fontFamily="primary"
          fontWeight="semibold"
          color="gray800"
          data-testid="dialog-print-header"
        >
          Print
        </Type>
      </DialogHeadingWrapper>
      <DialogSubHeadingWrapper>
        <DialogContentHeader>
          <Type
            element="h3"
            fontFamily="primary"
            fontWeight="semibold"
            color="gray700"
            data-testid="dialog-print-sub-header"
          >
            Topic
          </Type>
          <div>
            <SelectButton
              onClick={handlePrintDialogSelectAll}
              disabled={
                selectedReferences.length === tocReferences?.length ||
                disablePrint
              }
              background={false}
              size="md"
              kind="text"
              data-testid="dialog-print-select-all"
            >
              Select All
            </SelectButton>
            <SelectButton
              onClick={handlePrintDialogDeselectAll}
              disabled={selectedReferences.length === 0 || disablePrint}
              background={false}
              size="md"
              kind="text"
              data-testid="dialog-print-deselect-all"
            >
              Deselect All
            </SelectButton>
          </div>
        </DialogContentHeader>
      </DialogSubHeadingWrapper>
      <DialogContentWrapper style={{ maxHeight }}>
        <DialogContentList data-testid="dialog-print-content-list">
          {data.map((item) => {
            const sectionId = item.reference || item.id;
            return (
              <DialogContentListItemParent
                key={sectionId}
                data-testid="dialog-print-content-list-item"
                hasSubTocs={item.subSection && item.subSection.length > 0}
              >
                <fieldset>
                  <legend>
                    <DialogParentToc
                      label={item.title}
                      name={item.title}
                      id={`${sectionId}-print`}
                      value={sectionId}
                      checked={selectedReferences.includes(sectionId)}
                      onChange={onParentChange}
                      data-testid="dialog-print-content-list-item-label"
                      disabled={disablePrint}
                    />
                  </legend>
                  {item.subSection && canSelectSubsectionsForPrint && (
                    <ul>
                      {item.subSection.map((subSection) => {
                        const subSectionId =
                          subSection.reference || subSection.id;
                        return (
                          <DialogContentListItemParent
                            key={subSection.reference}
                            data-testid="dialog-print-content-list-item"
                          >
                            <DialogChildToc
                              label={subSection.title}
                              name={subSection.title}
                              id={`${subSectionId}-print`}
                              value={subSectionId}
                              checked={selectedReferences.includes(
                                subSectionId
                              )}
                              onChange={onChildChange}
                              data-testid="dialog-print-content-list-child-item-label"
                              disabled={disablePrint}
                            />
                          </DialogContentListItemParent>
                        );
                      })}
                    </ul>
                  )}
                </fieldset>
              </DialogContentListItemParent>
            );
          })}
        </DialogContentList>
      </DialogContentWrapper>
      <DialogContentFooter data-testid="dialog-print-footer">
        <Button
          onClick={handleOnPrint}
          size="md"
          disabled={selectedReferences.length === 0 || disablePrint}
          data-testid="dialog-print-footer-print-button"
        >
          Print
        </Button>
        <Button
          onClick={onCancel}
          size="md"
          kind="text"
          data-testid="dialog-print-footer-cancel-button"
          style={{ marginLeft: ".5rem" }}
        >
          Cancel
        </Button>
      </DialogContentFooter>
    </DialogWrapper>
  ) : (
    <></>
  );
};

ContentPrintDialog.propTypes = {
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
  onParentChange: PropTypes.func.isRequired,
  onChildChange: PropTypes.func.isRequired,
  handlePrintDialogSelectAll: PropTypes.func.isRequired,
  handlePrintDialogDeselectAll: PropTypes.func.isRequired,
  selectedReferences: PropTypes.array,
  tocData: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      reference: PropTypes.string.isRequired,
      children: PropTypes.array,
    })
  ),
};

ContentPrintDialog.defaultProps = {
  isOpen: false,
  selectedReferences: [],
};

ContentPrintDialog.displayName = "ContentPrintDialog";
export default ContentPrintDialog;
